// frontend/src/components/client/ClientChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, User, CheckCheck, Phone, Video, Bot, Headphones } from 'lucide-react';
import { sendClientMessage, sendAssistantMessage } from '../../services/clientApi';

import { useClientAuth } from '../../contexts/ClientAuthContext';

// =============================================================================
// MARKDOWN RENDERER — supporte **bold**, *italic*, \n, puces - et •
// Filtre aussi le JSON brut si le backend en envoie par erreur
// =============================================================================
const renderMessage = (text) => {
  if (!text) return null;

  // Guard: si le reply est du JSON brut (outil non dispatché côté backend),
  // afficher un message d'erreur propre au lieu du JSON
  const trimmed = text.trim();
  if (trimmed.startsWith('{') && trimmed.includes('"tool"')) {
    return (
      <span className="text-yellow-400 text-xs">
        ⚠️ Internal error: the assistant returned raw data. Please try again.
      </span>
    );
  }

  // Parser le markdown minimal ligne par ligne
  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  for (const line of lines) {
    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    // Ligne puce (- ou • ou *)
    const bulletMatch = line.match(/^[\s]*[-•*]\s+(.+)/);
    if (bulletMatch) {
      elements.push(
        <div key={key++} className="flex items-start gap-1.5 my-0.5">
          <span className="text-purple-400 mt-0.5 flex-shrink-0">•</span>
          <span>{inlineFormat(bulletMatch[1])}</span>
        </div>
      );
      continue;
    }

    // Ligne numérotée (1. 2. etc.)
    const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s+(.+)/);
    if (numberedMatch) {
      elements.push(
        <div key={key++} className="flex items-start gap-1.5 my-0.5">
          <span className="text-purple-400 flex-shrink-0">{numberedMatch[1]}.</span>
          <span>{inlineFormat(numberedMatch[2])}</span>
        </div>
      );
      continue;
    }

    // Ligne normale
    elements.push(
      <div key={key++} className="leading-relaxed">
        {inlineFormat(line)}
      </div>
    );
  }

  return <>{elements}</>;
};

// Formatte **bold**, *italic*, `code` dans une ligne
const inlineFormat = (text) => {
  // Découper sur les marqueurs markdown
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="bg-gray-900 text-purple-300 px-1 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
const ClientChat = () => {
  const { client } = useClientAuth(); // ajouter cet import en haut
  const storageKey = `assistant_chat_history_${client?.id || 'unknown'}`;
  const [mode, setMode] = useState(() => localStorage.getItem('chat_mode') || 'support');

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [staffOnline, setStaffOnline] = useState(false);
  const [assistantThinking, setAssistantThinking] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const textareaRef = useRef(null);
  const sessionIdRef = useRef(Date.now().toString());

  useEffect(() => { localStorage.setItem('chat_mode', mode); }, [mode]);

  useEffect(() => {
    if (mode === 'support') {
      connectWebSocket();
    } else {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      setStaffOnline(false);
      setLoading(false);
      loadAssistantHistory();
    }
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [mode, client?.id]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const loadAssistantHistory = () => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try { setMessages(JSON.parse(saved)); } catch (e) {}
    }
    setLoading(false);
  };

  const saveAssistantHistory = (newMessages) => {
    localStorage.setItem(storageKey, JSON.stringify(newMessages.slice(-50)));
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('client_access_token');
    if (!token) return;

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
    const wsBase = API_BASE
      .replace(/^https/, 'wss')
      .replace(/^http/, 'ws')
      .replace(/\/api\/?$/, '');

    wsRef.current = new WebSocket(`${wsBase}/api/client/chat/ws?token=${token}`);

    wsRef.current.onopen = () => { setStaffOnline(true); setLoading(false); };
    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setMessages(prev => [...prev, {
          id: data.id, message: data.message,
          sender_type: data.sender_type, created_at: data.created_at, is_read: data.is_read
        }]);
      } catch (e) {}
    };
    wsRef.current.onclose = (event) => {
      setStaffOnline(false);
      if (event.code === 1008 || event.code === 1011) return;
      setTimeout(connectWebSocket, 3000);
    };
    wsRef.current.onerror = () => setLoading(false);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    const userMessage = inputMessage.trim();
    const tempId = `temp_${Date.now()}`;

    const userMsg = {
      id: tempId, message: userMessage,
      sender_type: 'client', created_at: new Date().toISOString(),
      is_read: false, is_temp: true
    };

    setMessages(prev => [...prev, userMsg]);
    if (mode === 'assistant') saveAssistantHistory([...messages, userMsg]);
    setInputMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    scrollToBottom();
    setSending(true);

    try {
      if (mode === 'assistant') {
        setAssistantThinking(true);
        const response = await sendAssistantMessage(userMessage, sessionIdRef.current);

        const assistantMsg = {
          id: `assistant_${Date.now()}`,
          message: response.data.reply,
          sender_type: 'assistant',
          created_at: new Date().toISOString(),
          is_read: true,
          quick_actions: response.data.quick_actions || [],
          actions_taken: response.data.actions_taken || [],
        };

        setMessages(prev => {
          const updated = prev.map(msg => msg.id === tempId ? { ...msg, is_temp: false } : msg);
          const withAssistant = [...updated, assistantMsg];
          saveAssistantHistory(withAssistant);
          return withAssistant;
        });
        setAssistantThinking(false);
      } else {
        const response = await sendClientMessage(userMessage);
        setMessages(prev => prev.map(msg => msg.id === tempId ? { ...response.data, is_temp: false } : msg));
      }
    } catch (error) {
      console.error('Send error:', error);
      setMessages(prev => prev.map(msg => msg.id === tempId ? { ...msg, is_temp: false, is_error: true } : msg));
      const errorMsg = {
        id: `error_${Date.now()}`,
        message: mode === 'assistant'
          ? '⚠️ AI assistant is currently unavailable. Please try again later or switch to human support.'
          : '❌ Failed to send. Please try again.',
        sender_type: 'system',
        created_at: new Date().toISOString(),
        is_error: true
      };
      setMessages(prev => [...prev, errorMsg]);
      if (mode === 'assistant') {
        saveAssistantHistory([...messages.map(m => m.id === tempId ? { ...m, is_temp: false, is_error: true } : m), errorMsg]);
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 640) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInput = (e) => {
    setInputMessage(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const diff = Date.now() - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  const clearAssistantHistory = () => {
    if (mode === 'assistant' && window.confirm('Clear conversation with assistant?')) {
      localStorage.removeItem('assistant_chat_history');
      const welcome = {
        id: 'welcome',
        message: "✈️ Hello! I'm Copilot Aero, your personal assistant.\n\nI can:\n• Calculate flight plans\n• Order fuel\n• Check weather and NOTAMs\n• Schedule maintenance\n\nWhat can I do for you today?",
        sender_type: 'assistant',
        created_at: new Date().toISOString(),
        is_read: true
      };
      setMessages([welcome]);
      saveAssistantHistory([welcome]);
    }
  };

  const QuickActionButton = ({ label, action, type }) => {
    const isLink = type === 'link' || /^https?:\/\//i.test(action);
    const handleClick = () => {
      if (isLink) {
        window.open(action, '_blank', 'noopener,noreferrer');
      } else {
        setInputMessage(action);
        setTimeout(() => sendMessage(), 100);
      }
    };
    return (
      <button
        onClick={handleClick}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-full text-xs text-gray-300 transition mr-2 mb-2"
      >
        {label}
      </button>
    );
  };

  // =============================================================================
  // RENDER D'UN MESSAGE — isole la logique d'affichage
  // =============================================================================
  const MessageBubble = ({ msg }) => {
    const isClient = msg.sender_type === 'client';
    const isAssistant = msg.sender_type === 'assistant';
    const isSystem = msg.sender_type === 'system';

    return (
      <div className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] sm:max-w-[70%] ${isClient ? 'items-end' : 'items-start'} flex flex-col`}>

          {/* Avatar + nom — seulement pour les messages non-client */}
          {!isClient && !isSystem && (
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isAssistant ? 'bg-purple-600' : 'bg-blue-600'}`}>
                {isAssistant ? <Bot className="w-3 h-3 text-white" /> : <User className="w-3 h-3 text-white" />}
              </div>
              <span className={`text-xs font-medium ${isAssistant ? 'text-purple-400' : 'text-blue-400'}`}>
                {isAssistant ? 'Copilot Aero' : 'FBO Support'}
              </span>
              <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
            </div>
          )}

          {/* Bulle du message */}
          <div className={`rounded-2xl px-4 py-2.5 ${
            isClient
              ? 'bg-blue-500 text-white rounded-tr-sm'
              : isSystem
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-tl-sm'
                : isAssistant
                  ? 'bg-purple-500/20 text-gray-200 border border-purple-500/30 rounded-tl-sm'
                  : 'bg-gray-700 text-gray-200 rounded-tl-sm'
          }`}>

            {/* Contenu du message — texte client brut, markdown pour assistant */}
            <div className="text-sm break-words">
              {isClient || isSystem
                ? <p className="whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                : renderMessage(msg.message)
              }
            </div>

            {/* Badge actions exécutées (vol calculé, event créé, etc.) */}
            {msg.actions_taken && msg.actions_taken.length > 0 && (
              <div className="mt-2 pt-1.5 border-t border-purple-500/20">
                {msg.actions_taken.map((action, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 text-xs text-purple-300 bg-purple-900/30 px-2 py-0.5 rounded-full mr-1">
                    {action.type === 'calculate_flight_plan' && '✈️ Flight plan calculated'}
                    {action.type === 'create_calendar_event' && '📅 Event added'}
                    {action.type === 'create_fuel_request' && '⛽ Fuel request created'}
                  </span>
                ))}
              </div>
            )}

            {/* Quick action buttons */}
            {msg.quick_actions && msg.quick_actions.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-600/50 flex flex-wrap">
                {msg.quick_actions.map((qa, idx) => (
                  <QuickActionButton key={idx} label={qa.label} action={qa.action} />
                ))}
              </div>
            )}
          </div>

          {/* Timestamp + lu — seulement pour les messages client */}
          {isClient && (
            <div className="flex items-center gap-1 mt-1 self-end">
              <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
              {!msg.is_temp && (
                <CheckCheck className={`w-3 h-3 ${msg.is_read ? 'text-blue-400' : 'text-gray-500'}`} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =============================================================================
  // JSX PRINCIPAL
  // =============================================================================
  return (
    <div className="flex flex-col px-4 sm:px-0" style={{ height: 'calc(100dvh - 160px)', minHeight: '400px' }}>

      {/* Header */}
      <div className="bg-gray-800/50 rounded-t-xl border border-gray-700 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${mode === 'assistant' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
              {mode === 'assistant'
                ? <Bot className="w-5 h-5 text-purple-400" />
                : <MessageSquare className="w-5 h-5 text-blue-400" />
              }
            </div>
            <div>
              <h2 className="text-white font-semibold text-sm sm:text-base">
                {mode === 'assistant' ? 'Copilot Aero' : 'FBO Support'}
              </h2>
              <div className="flex items-center gap-1 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${
                  mode === 'assistant' ? 'bg-purple-500 animate-pulse'
                    : staffOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'
                }`} />
                <span className="text-xs text-gray-400">
                  {mode === 'assistant' ? 'AI Assistant online'
                    : staffOnline ? 'Staff online' : 'Staff offline'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setMode(mode === 'assistant' ? 'support' : 'assistant')}
              className={`p-2 rounded-lg transition flex items-center gap-1 ${
                mode === 'assistant' ? 'bg-purple-500/30 text-purple-400' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
              title={mode === 'assistant' ? 'Switch to human support' : 'Switch to AI assistant'}
            >
              {mode === 'assistant' ? <Headphones className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              <span className="text-xs hidden sm:inline">
                {mode === 'assistant' ? 'Support' : 'AI'}
              </span>
            </button>
            <button className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition">
              <Phone className="w-4 h-4" />
            </button>
            <button className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition">
              <Video className="w-4 h-4" />
            </button>
            {mode === 'assistant' && (
              <button
                onClick={clearAssistantHistory}
                className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition"
                title="New conversation"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Zone messages */}
      <div className="flex-1 bg-gray-800/30 border-x border-gray-700 overflow-y-auto p-3 sm:p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {mode === 'assistant'
              ? <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
              : <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            }
            <p>{mode === 'assistant' ? 'Ask me anything!' : 'No messages yet'}</p>
            <p className="text-xs mt-1">
              {mode === 'assistant'
                ? 'I can calculate flight plans, order fuel, check weather...'
                : 'Send a message to start the conversation'}
            </p>
            {mode === 'assistant' && (
              <div className="mt-4 flex flex-wrap justify-center">
                <QuickActionButton label="✈️ Flight 5C1 → KDFW" action="Calculate flight from 5C1 to KDFW" />
                <QuickActionButton label="⛽ 100 gallons" action="Order 100 gallons for my plane tomorrow morning" />
                <QuickActionButton label="☁️ Weather KDFW" action="Weather at KDFW" />
              </div>
            )}
          </div>
        ) : (
          messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
        )}

        {/* Indicateur de frappe */}
        {assistantThinking && (
          <div className="flex justify-start">
            <div className="bg-purple-500/20 border border-purple-500/30 rounded-2xl rounded-tl-sm px-4 py-2.5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Zone de saisie */}
      <div className="bg-gray-800/50 rounded-b-xl border border-gray-700 p-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={handleInput}
            onKeyPress={handleKeyPress}
            placeholder={mode === 'assistant' ? 'Ask your question...' : 'Type your message...'}
            rows="1"
            className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-white resize-none focus:outline-none focus:border-blue-500 text-sm leading-relaxed"
            style={{ maxHeight: '120px', overflowY: 'auto' }}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !inputMessage.trim()}
            className={`p-3 rounded-xl text-white disabled:opacity-50 transition flex-shrink-0 ${
              mode === 'assistant' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {sending
              ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Send className="w-5 h-5" />
            }
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center hidden sm:block">
          {mode === 'assistant'
            ? 'The assistant can order fuel, calculate flights, check weather and NOTAMs'
            : 'Our team typically responds within 15 minutes during business hours'}
        </p>
      </div>
    </div>
  );
};

export default ClientChat;